import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchArtistQuote } from "../api/artistQuoteService";

const localeFor = (language) => {
  if (language?.startsWith("de")) return "de-DE";
  if (language?.startsWith("en")) return "en-GB";
  return "fr-FR";
};

export default function FormatAnalysis({ artistName }) {
  const { t, i18n } = useTranslation();
  const [formats, setFormats] = useState([]);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    async function loadFormatData() {
      const data = await fetchArtistQuote(artistName);
      if (!data) return;

      const categorized = {
        petit: [],
        moyen: [],
        grand: [],
      };

      data.forEach((item) => {
        const surface = Number(item.surface_m2) || 0;
        if (surface < 0.3) categorized.petit.push(item);
        else if (surface < 1.0) categorized.moyen.push(item);
        else categorized.grand.push(item);
      });

      const result = Object.entries(categorized).map(([key, group]) => {
        const average = group.length
          ? group.reduce((sum, item) => sum + Number.parseFloat(item.average_value || 0), 0) / group.length
          : null;
        return { format: key, average, count: group.length };
      });

      setFormats(result);
    }
    loadFormatData();
  }, [artistName]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold mb-4">🖼️ {t("artistQuoteTools.formatAnalysis.title")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {formats.map((item) => (
          <div key={item.format} className="border rounded p-4 shadow-sm bg-white">
            <p className="font-medium capitalize">
              {t("artistQuoteTools.formatAnalysis.formatLabel", {
                format: t(`artistQuoteTools.formatAnalysis.formats.${item.format}`),
              })}
            </p>
            {item.average !== null ? (
              <p className="text-sm text-gray-700">
                {t("artistQuoteTools.formatAnalysis.averagePrice", {
                  value: formatCurrency(item.average),
                  count: item.count,
                })}
              </p>
            ) : (
              <p className="text-sm text-gray-400">{t("artistQuoteTools.formatAnalysis.noData")}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
